import React from 'react';
import { TranslationKey } from '../../../contexts/I18nContext';
import {
  ProgrammingCommand, ProgrammingChange, ReadItem, ScalarSpec, CompositeSpec, ValueSpec,
} from '../../../services/programmingService';

/*
 * How a gateway parameter is shown and edited, from its catalogue spec:
 * the read value as text or as a small inline structure, the matching
 * input in write mode, and the confirmation dialog listing what will be
 * sent, before and after.
 */

export type Translate = (key: TranslationKey, vars?: Record<string, string | number>) => string;

export const SECRET = '••••';

// ---------------------------------------------------------------- as text
const enumText = (spec: ScalarSpec, value: unknown): string => {
  const key = String(value);
  const label = spec.enum?.[key];
  return label ? `${label} (${key})` : key;
};

/** A value as one line of text: for the confirmation dialog and the "before" hint. */
export const valueText = (spec: ValueSpec | null | undefined, value: unknown, secret = false): string => {
  if (secret) return SECRET;
  if (value === null || value === undefined || value === '') return '–';
  if (!spec) return typeof value === 'object' ? JSON.stringify(value) : String(value);
  if (spec.type === 'composite') {
    const record = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
    return spec.parts
      .map((part) => `${part.label} : ${part.secret ? SECRET : part.enum ? enumText(part, record[part.name] ?? '') : String(record[part.name] ?? '–')}`)
      .join(' · ');
  }
  if (spec.type === 'enum') return enumText(spec, value);
  if (Array.isArray(value)) return value.join(', ');
  return `${String(value)}${spec.unit ? ` ${spec.unit}` : ''}`;
};

// ---------------------------------------------------------------- display
const EnumValue: React.FC<{ spec: ScalarSpec; value: unknown }> = ({ spec, value }) => {
  const key = String(value);
  const label = spec.enum?.[key];
  return label ? <>{label} <span className="prog-code">{key}</span></> : <>{key}</>;
};

interface DisplayProps {
  command: ProgrammingCommand;
  item: ReadItem | undefined;
  t: Translate;
}

/** The read value, as the table shows it. */
export const DisplayValue: React.FC<DisplayProps> = ({ command, item, t }) => {
  if (!command.ops.includes('read')) return <span className="prog-value-none">{t('devices.programming.writeOnly')}</span>;
  if (!item || item.status !== 'ok') return <span className="prog-value-none">–</span>;
  if (command.secret) return <span className="prog-value-secret">{SECRET}</span>;
  const spec = command.value;
  const value = item.value;
  if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
    return <span className="prog-value-none">{t('devices.programming.noValue')}</span>;
  }
  if (!spec) return <>{String(value)}</>;
  if (spec.type === 'composite') {
    const record = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
    return (
      <span className="prog-kv-list">
        {spec.parts.map((part) => (
          <span key={part.name} className="prog-kv">
            <span className="prog-kv-key">{part.label}</span>
            {part.secret ? SECRET : part.enum ? <EnumValue spec={part} value={record[part.name] ?? ''} /> : String(record[part.name] ?? '–')}
          </span>
        ))}
      </span>
    );
  }
  if (spec.type === 'enum') return <EnumValue spec={spec} value={value} />;
  if (spec.type === 'phone_list') {
    const list = Array.isArray(value) ? value : [value];
    return <span className="prog-chips">{list.map((v, i) => <span key={i} className="prog-chip">{String(v)}</span>)}</span>;
  }
  if (spec.multiline) return <pre className="prog-multiline">{String(value)}</pre>;
  return <>{String(value)}{spec.unit ? <span className="prog-unit"> {spec.unit}</span> : null}</>;
};

// ---------------------------------------------------------------- inputs
interface ScalarInputProps {
  spec: ScalarSpec;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  id: string;
}

export const ScalarInput: React.FC<ScalarInputProps> = ({ spec, value, onChange, disabled, id }) => {
  const text = value === undefined || value === null ? '' : Array.isArray(value) ? value.join(', ') : String(value);
  if (spec.type === 'enum') {
    return (
      <select id={id} className="prog-input" value={text} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        <option value="">–</option>
        {Object.entries(spec.enum ?? {}).map(([key, label]) => (
          <option key={key} value={key}>{label} ({key})</option>
        ))}
      </select>
    );
  }
  if (spec.type === 'int') {
    const range = spec.min !== undefined && spec.max !== undefined ? `${spec.min} – ${spec.max}` : spec.min !== undefined ? `≥ ${spec.min}` : spec.max !== undefined ? `≤ ${spec.max}` : null;
    return (
      <span className="prog-input-group">
        <span className="prog-input-unit">
          <input id={id} type="number" className="prog-input prog-input-short" value={text} min={spec.min} max={spec.max}
                 placeholder={spec.default !== undefined ? String(spec.default) : undefined}
                 onChange={(e) => onChange(e.target.value)} disabled={disabled} />
          {spec.unit && <span className="prog-unit">{spec.unit}</span>}
        </span>
        {range && <span className="prog-help">{range}</span>}
      </span>
    );
  }
  const type = spec.secret ? 'password' : spec.type === 'phone' ? 'tel' : 'text';
  const placeholder = spec.type === 'time_hhmm' ? 'HHMM' : spec.type === 'phone_list' ? '33…, 33…' : spec.default !== undefined ? String(spec.default) : undefined;
  return (
    <input id={id} type={type} className={`prog-input${spec.type === 'phone' || spec.type === 'phone_list' || spec.type === 'time_hhmm' ? ' prog-input-mono' : ''}`}
           value={text} placeholder={placeholder} maxLength={spec.max_length}
           onChange={(e) => onChange(e.target.value)} disabled={disabled} autoComplete="off" />
  );
};

interface CompositeInputProps {
  spec: CompositeSpec;
  value: Record<string, unknown> | undefined;
  onChange: (value: Record<string, unknown>) => void;
  disabled?: boolean;
  id: string;
}

export const CompositeInput: React.FC<CompositeInputProps> = ({ spec, value, onChange, disabled, id }) => (
  <div className="prog-composite-inputs">
    {spec.parts.map((part) => (
      <label key={part.name} className="prog-part">
        <span>{part.label}</span>
        <ScalarInput id={`${id}-${part.name}`} spec={part} value={value?.[part.name]}
                     onChange={(v) => onChange({ ...(value ?? {}), [part.name]: v })} disabled={disabled} />
      </label>
    ))}
  </div>
);

// ---------------------------------------------------------------- confirm
interface ConfirmProps {
  changes: ProgrammingChange[];
  commands: Record<string, ProgrammingCommand>;
  values: Record<string, ReadItem>;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
  t: Translate;
}

/** What is about to be sent: one line per command, with the value before and after. */
export const ConfirmChanges: React.FC<ConfirmProps> = ({ changes, commands, values, onConfirm, onCancel, busy, t }) => {
  const dangerous = changes.some((c) => commands[c.code]?.dangerous);
  return (
    <div className="prog-confirm" role="dialog" aria-modal="true" data-testid="programming-confirm">
      <div className="prog-confirm-box">
        <h4>{t('devices.programming.confirmTitle')}</h4>
        <table className="prog-confirm-table">
          <thead>
            <tr>
              <th>{t('devices.programming.columns.parameter')}</th>
              <th>{t('devices.programming.confirmOperation')}</th>
              <th>{t('devices.programming.before')}</th>
              <th>{t('devices.programming.after')}</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c) => {
              const command = commands[c.code];
              const current = values[c.code];
              const before = command && current?.status === 'ok' ? valueText(command.value, current.value, command.secret) : '–';
              const after = c.op === 'write' ? valueText(command?.value, c.value, command?.secret) : '';
              return (
                <tr key={`${c.code}-${c.op}`} className={command?.dangerous ? 'dangerous' : undefined}>
                  <td>
                    <strong>{command?.label ?? c.code}</strong>
                    <span className="prog-code">{command?.cmd ?? c.code}{c.index !== undefined ? `[${c.index}]` : ''}</span>
                    {command?.dangerous && <span className="prog-flag" aria-label={t('devices.programming.dangerous')}>⚠</span>}
                  </td>
                  <td><span className="prog-op">{t(`devices.programming.op.${c.op}` as const)}</span></td>
                  <td className="prog-confirm-value">{c.op === 'write' ? before : '–'}</td>
                  <td className="prog-confirm-value">{c.op === 'write' ? <strong>{after}</strong> : <span className="prog-value-none">{t(`devices.programming.opEffect.${c.op}` as const)}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {dangerous && <p className="prog-notice prog-notice-error">{t('devices.programming.dangerousWarning')}</p>}
        <div className="prog-confirm-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel} disabled={busy}>{t('common.cancel')}</button>
          <button type="button" className={`btn btn-sm ${dangerous ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm} disabled={busy} data-testid="programming-confirm-send">
            {busy ? t('devices.programming.sending') : t('devices.programming.send')}
          </button>
        </div>
      </div>
    </div>
  );
};
